import {
Cancel as CancelIcon,CheckCircle as CheckIcon,EmojiEvents as TrophyIcon,Refresh as RefreshIcon,School as SchoolIcon,
TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import {
Alert,Box,Button,Card,
CardContent,Chip,
LinearProgress,
List,
ListItem,ListItemIcon,ListItemText,Paper,Typography
} from '@mui/material';
import React from 'react';


interface QuizAnswer {
  question_type?: string;
  question_number: number;
  question_text: string;
  user_answer: string;
  correct_answer: string;
  is_correct: boolean;
  time_spent: number;
  explanation: string;
}

interface QuizResultsProps {
  sessionId: string;
  totalQuestions: number;
  correctAnswers: number;
  score: number;
  answers: QuizAnswer[];
  onRestart: () => void;
  onNewQuiz: () => void;
  onClose: () => void;
}

const QuizResults: React.FC<QuizResultsProps> = ({
  sessionId,
  totalQuestions,
  correctAnswers,
  score,
  answers,
  onRestart,
  onNewQuiz,
  onClose,
}) => {
  const wrongAnswers = totalQuestions - correctAnswers;
  const percentage = score;

  const getScoreLevel = (score: number): { level: string; color: string; icon: React.ReactNode } => {
    if (score >= 90) {
      return {
        level: 'Отлично!',
        color: '#4caf50',
        icon: <TrophyIcon sx={{ color: '#ffd700' }} />
      };
    } else if (score >= 80) {
      return {
        level: 'Хорошо!',
        color: '#2196f3',
        icon: <TrendingUpIcon sx={{ color: '#2196f3' }} />
      };
    } else if (score >= 70) {
      return {
        level: 'Удовлетворительно',
        color: '#ff9800',
        icon: <SchoolIcon sx={{ color: '#ff9800' }} />
      };
    } else {
      return {
        level: 'Нужно подучить',
        color: '#f44336',
        icon: <SchoolIcon sx={{ color: '#f44336' }} />
      };
    }
  };

  const scoreLevel = getScoreLevel(score);

  const getRecommendations = (score: number): string[] => {
    if (score >= 90) {
      return [
        'Превосходный результат!',
        'Попробуйте более сложные категории терминов',
        'Поделитесь знаниями с другими'
      ];
    } else if (score >= 80) {
      return [
        'Хороший результат!',
        'Повторите термины, на которые ответили неправильно',
        'Попробуйте викторину с другой категорией'
      ];
    } else if (score >= 70) {
      return [
        'Неплохой результат',
        'Изучите определения терминов подробнее',
        'Повторите материал перед следующей викториной'
      ];
    } else {
      return [
        'Нужно больше практики',
        'Изучите базовые термины перед викториной',
        'Используйте режим обучения для повторения'
      ];
    }
  };

  const recommendations = getRecommendations(score);

  const getAverageTime = (): number => {
    const totalTime = answers.reduce((sum, answer) => sum + answer.time_spent, 0);
    return answers.length ? Math.round(totalTime / answers.length) : 0;
  };

  const getCategoryStats = (): { [key: string]: { correct: number; total: number } } => {
    const stats: { [key: string]: { correct: number; total: number } } = {};

    answers.forEach(answer => {
      const types: Record<string,string> = {definition:'Определения',transliteration:'Транслитерация',category:'Категории',arabic_term:'Арабские термины'};
      const category = types[answer.question_type || ''] || 'Тип не указан';

      if (!stats[category]) {
        stats[category] = { correct: 0, total: 0 };
      }
      stats[category].total++;
      if (answer.is_correct) {
        stats[category].correct++;
      }
    });

    return stats;
  };

  const categoryStats = getCategoryStats();

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 2 }}>
      {/* Основные результаты */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box textAlign="center" mb={3}>
            <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
              {scoreLevel.icon}
              <Typography variant="h4" sx={{ ml: 1, color: scoreLevel.color }}>
                {scoreLevel.level}
              </Typography>
            </Box>

            <Typography variant="h2" sx={{ fontWeight: 'bold', color: scoreLevel.color }}>
              {score}%
            </Typography>

            <Typography variant="h6" color="text.secondary">
              {correctAnswers} из {totalQuestions} правильных ответов
            </Typography>
          </Box>

          {/* Прогресс-бар */}
          <Box mb={3}>
            <LinearProgress
              variant="determinate"
              value={percentage}
              sx={{
                height: 20,
                borderRadius: 10,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: scoreLevel.color,
                  borderRadius: 10,
                }
              }}
            />
          </Box>

          {/* Статистика */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Paper sx={{ p: 2, textAlign: 'center', flex: 1, minWidth: '200px' }}>
              <Typography variant="h6" color="success.main">
                {correctAnswers}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Правильных
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: 'center', flex: 1, minWidth: '200px' }}>
              <Typography variant="h6" color="error.main">
                {wrongAnswers}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Неправильных
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: 'center', flex: 1, minWidth: '200px' }}>
              <Typography variant="h6" color="primary.main">
                {getAverageTime()}с
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Среднее время
              </Typography>
            </Paper>
          </Box>

          {/* Рекомендации */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Рекомендации:
            </Typography>
            {recommendations.map((rec, index) => (
              <Typography key={index} variant="body2">
                • {rec}
              </Typography>
            ))}
          </Alert>

          {/* Кнопки действий */}
          <Box display="flex" gap={2} justifyContent="center">
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={onRestart}
            >
              Повторить
            </Button>
            <Button
              variant="outlined"
              onClick={onNewQuiz}
            >
              Новая викторина
            </Button>
            <Button
              variant="text"
              onClick={onClose}
            >
              Закрыть
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Статистика по категориям */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Статистика по типам вопросов
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {Object.entries(categoryStats).map(([category, stats]) => {
              const percentage = Math.round((stats.correct / stats.total) * 100);
              return (
                <Box key={category} sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {category}
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      {percentage}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stats.correct}/{stats.total}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={percentage}
                      sx={{ mt: 1 }}
                    />
                  </Paper>
                </Box>
              );
            })}
          </Box>
        </CardContent>
      </Card>

      {/* Детальные результаты */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Детальные результаты
          </Typography>
          <List>
            {answers.map((answer, index) => (
              <ListItem key={index} divider>
                <ListItemIcon>
                  {answer.is_correct ? (
                    <CheckIcon color="success" />
                  ) : (
                    <CancelIcon color="error" />
                  )}
                </ListItemIcon>
                <ListItemText
                  disableTypography
                  primary={
                    <Box>
                      <Typography variant="body1" component="span">
                        {answer.question_text}
                      </Typography>
                      <Chip
                        label={`${answer.time_spent}с`}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Ваш ответ: {answer.user_answer || 'Не отвечено'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Правильный ответ: {answer.correct_answer}
                      </Typography>
                      {answer.explanation && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          <strong>Объяснение:</strong> {answer.explanation}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default QuizResults;
